// --- START OF FILE NotificationContext.js ---
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { initializeApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  isSupported,
  deleteToken,
} from "firebase/messaging";
import { useAuth } from "./AuthContext";

/* ---------- Firebase config ----- */
const firebaseConfig = {
  apiKey: "AIzaSyDLElVRHDik_iB1-TtMFzBvtbDJeB6uDso",
  authDomain: "ios-notifications-6bad9.firebaseapp.com",
  projectId: "ios-notifications-6bad9",
  storageBucket: "ios-notifications-6bad9.firebasestorage.app",
  messagingSenderId: "175706086324",
  appId: "1:175706086324:web:269459d8f051396c2ab0e1"
};

/* ---------- Helpers --------------------------------------------------- */
const ANDROID_APP =
  /Android/i.test(navigator.userAgent) &&
  window.matchMedia("(display-mode: standalone)").matches;
const IOS_APP =
  !!(
    window.webkit &&
    window.webkit.messageHandlers &&
    window.webkit.messageHandlers["push-permission-request"]
  );

/* ---------------------------------------------------------------------- */
const NotificationContext = createContext({
  permission: "default",
  iosPermissionStatus: "notDetermined",
  isSubscribed: false,
  shouldShowPrompt: false,
  fcmToken: null,
  fcmError: null,
  requestPermissionAndSubscribe: async () => false,
  unsubscribe: async () => false,
  deferPrompt: () => {},
  saveFCMTokenToBackend: async (token, platform) => false,
  requestIOSTokenManually: () => {}, // New function
});

export const useNotification = () => useContext(NotificationContext);

/* ---------- Provider --------------------------------------------------- */
export const NotificationProvider = ({ children }) => {
  const { user, refreshUserProfile } = useAuth();

  const [permission, setPermission] = useState("default");
  const [iosPermissionStatus, setIosPermissionStatus] = useState("notDetermined");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [shouldShowPrompt, setShouldShowPrompt] = useState(false);
  const [fcmToken, setFcmToken] = useState(null);
  const [fcmError, setFcmError] = useState(null);
  const [initialIOSCheckDone, setInitialIOSCheckDone] = useState(!IOS_APP);
  const [iosTokenRequestedManually, setIosTokenRequestedManually] = useState(false); // Track manual request

  const ensureFirebase = useCallback(async () => {
    if (!ANDROID_APP) return null; // Only for Android/Web
    if (!isSupported || !(await isSupported())) {
      console.warn("[NotificationContext] Firebase Messaging not supported by this browser.");
      return null;
    }

    if (!window._firebaseApp) {
      console.log("[NotificationContext] Initializing Firebase app for client.");
      window._firebaseApp = initializeApp(firebaseConfig); // From firebase/app
      window._messaging = getMessaging(window._firebaseApp); // From firebase/messaging
    }

    // Get Service Worker Registration for firebase-messaging-sw.js
    // This assumes firebase-messaging-sw.js is at the root.
    try {
      // navigator.serviceWorker.ready ensures the active SW is ready.
      // If you have multiple SWs, you might need to be more specific or ensure
      // firebase-messaging-sw.js is the one controlling the push scope.
      // For simplicity, we assume one primary SW or that firebase-messaging-sw.js is correctly registered.
      const swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
      console.log('[NotificationContext] Service Worker for FCM registered/retrieved:', swRegistration);
      window._swReg = swRegistration; // Store it globally or pass it around
      return { messaging: window._messaging, swRegistration };
    } catch (error) {
      console.error('[NotificationContext] Service Worker registration failed:', error);
      setFcmError(`Android: Service Worker registration failed: ${error.message}`);
      return null;
    }
  }, []); // ANDROID_APP is const

  const saveFCMTokenToBackend = useCallback(async (token, platform = 'android') => {
    if (!token || !user) {
      console.log("[NotificationContext] Token or user not available for saveFCMTokenToBackend.", { hasToken: !!token, hasUser: !!user });
      return false;
    }
    if (token.startsWith("ERROR_")) {
      console.warn("[NotificationContext] Attempted to save an error string as token:", token);
      return false;
    }
    
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/auth/fcm/save-token/`, // Your working URL
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ token, platform }),
        }
      );
      
      if (!res.ok) {
          const errorData = await res.json().catch(() => ({ detail: `HTTP ${res.status}: ${res.statusText}` }));
          throw new Error(errorData.detail || `HTTP ${res.status}: ${res.statusText}`);
      }
      
      console.log(`[NotificationContext] FCM token (${platform}) saved successfully to backend.`);
      setIsSubscribed(true);
      setFcmError(null); 
      refreshUserProfile?.();
      return true;
    } catch (err) {
      console.error("[NotificationContext] Error in saveFCMTokenToBackend (dedicated endpoint):", err);
      return false;
    }
  }, [user, refreshUserProfile]);

  const requestIOSTokenManually = useCallback(() => {
    if (IOS_APP && window.webkit?.messageHandlers?.["request-fcm-token-from-swift"]) {
        console.log("[NotificationContext] Manually requesting FCM token from Swift via 'request-fcm-token-from-swift'.");
        window.webkit.messageHandlers["request-fcm-token-from-swift"].postMessage("get-my-token"); // Body can be anything, Swift just needs the message name
        setIosTokenRequestedManually(true);
    } else if (IOS_APP) {
        console.warn("[NotificationContext] Cannot manually request iOS token: 'request-fcm-token-from-swift' handler not found.");
    }
  }, []); // IOS_APP is const

  /* ------------ iOS Permission State & Result Handler -------------------- */
  useEffect(() => {
    if (!IOS_APP) return;

    const handleIOSPermissionInfo = (event) => {
      const state = event.detail; // "granted", "denied", "notDetermined"
      console.log("[Context] iOS permission info from Swift:", state, "Event type:", event.type);
      
      let newStatus = "notDetermined";
      if (state === "authorized" || state === "granted") newStatus = "granted";
      else if (state === "denied") newStatus = "denied";
      
      setIosPermissionStatus(newStatus);

      if (event.type === "push-permission-state") {
          setInitialIOSCheckDone(true);
          // If permission is already granted when we get the initial state,
          // and we haven't received a token or requested it yet, request it now.
          if (newStatus === "granted" && !fcmToken && !iosTokenRequestedManually) {
            console.log("[Context] Initial iOS permission is 'granted'. Requesting token manually.");
            requestIOSTokenManually();
          }
      }
      // If permission was just granted via request, also try to get token
      if (event.type === "push-permission-request" && newStatus === "granted" && !fcmToken && !iosTokenRequestedManually) {
          console.log("[Context] iOS permission just granted via request. Requesting token manually.");
          requestIOSTokenManually();
      }
    };

    window.addEventListener("push-permission-state", handleIOSPermissionInfo);
    window.addEventListener("push-permission-request", handleIOSPermissionInfo); // Listens to responses for permission requests

    if (window.webkit?.messageHandlers?.["push-permission-state"]) {
        console.log("[Context] Requesting initial iOS permission state from Swift...");
        window.webkit.messageHandlers["push-permission-state"].postMessage("get-state");
    } else {
        console.warn("[Context] iOS 'push-permission-state' handler not found. Manual token request might be delayed.");
        setInitialIOSCheckDone(true); // Assume done if handler isn't there
        // Consider requesting token here if no specific state check is possible
        if (!fcmToken && !iosTokenRequestedManually) {
            // A bit risky without knowing permission state, but a fallback
            // requestIOSTokenManually(); 
        }
    }

    return () => {
      window.removeEventListener("push-permission-state", handleIOSPermissionInfo);
      window.removeEventListener("push-permission-request", handleIOSPermissionInfo);
    };
  }, [IOS_APP, fcmToken, requestIOSTokenManually, iosTokenRequestedManually]); // Added fcmToken and request dependencies

  /* ------------ iOS Token Handler (for event 'push-token') ------------- */
  useEffect(() => {
    if (!IOS_APP) return;

    const handleIOSTokenEvent = async (event) => {
      const rawTokenDetail = event.detail;
      console.log("[Context] Received 'push-token' event from Swift. Detail:", rawTokenDetail);

      if (typeof rawTokenDetail === 'string') {
        const tokenOrErrorString = rawTokenDetail;

        if (tokenOrErrorString.startsWith("ERROR_")) {
          const errorMsg = `iOS: ${tokenOrErrorString}`;
          console.error(`[Context] iOS FCM token error reported by Swift: ${tokenOrErrorString}`);
          setFcmToken(null);
          setFcmError(errorMsg);
          setIsSubscribed(false); // If token acquisition fails, we are not subscribed
          return;
        }

        if (tokenOrErrorString && tokenOrErrorString.length > 0) {
          console.log("[Context] iOS FCM token received via event:", tokenOrErrorString);
          setFcmToken(tokenOrErrorString); // Set the token
          setFcmError(null);         // Clear any previous token acquisition error
          setIosPermissionStatus("granted"); // If we get a token, permission must be granted

          if (user) {
            console.log("[Context] User logged in. Attempting to save iOS FCM token immediately after receiving it.");
            await saveFCMTokenToBackend(tokenOrErrorString, 'ios');
          } else {
            console.log("[Context] User not yet logged in. iOS FCM token received and stored; will be saved upon login.");
          }
        } else {
          const errorMsg = "iOS: Received an unexpected empty non-error token string from Swift.";
          console.error(errorMsg, "Raw detail:", rawTokenDetail);
          setFcmToken(null);
          setFcmError(errorMsg);
          setIsSubscribed(false);
        }
      } else {
        const errorMsg = `iOS: Invalid data type for token/error from 'push-token' event. Expected string, got ${typeof rawTokenDetail}.`;
        console.error(errorMsg, "Raw detail:", rawTokenDetail);
        setFcmToken(null);
        setFcmError(errorMsg);
        setIsSubscribed(false);
      }
    };

    window.addEventListener("push-token", handleIOSTokenEvent);
    console.log("[Context] Added 'push-token' event listener for iOS.");

    // Fallback: If after a short delay no token has arrived, and permission IS granted,
    // and we haven't manually requested yet, try requesting.
    // This handles cases where the initial auto-emission from Swift might be missed.
    const timerId = setTimeout(() => {
        if (IOS_APP && !fcmToken && iosPermissionStatus === "granted" && !fcmError && !iosTokenRequestedManually) {
            console.log("[Context] Timeout: No FCM token received yet, permission is granted. Manually requesting token.");
            requestIOSTokenManually();
        }
    }, 3000); // 3 seconds delay for this fallback request

    return () => {
      window.removeEventListener("push-token", handleIOSTokenEvent);
      console.log("[Context] Removed 'push-token' event listener for iOS.");
      clearTimeout(timerId);
    };
  }, [IOS_APP, user, saveFCMTokenToBackend, fcmToken, iosPermissionStatus, fcmError, requestIOSTokenManually, iosTokenRequestedManually]); // Added dependencies

  /* ------------ Initial check (Android: permission + token) ------------- */
  useEffect(() => {
    if (!ANDROID_APP) return;

    (async () => {
      const firebaseEssentials = await ensureFirebase(); // This now returns { messaging, swRegistration }
      if (!firebaseEssentials || !firebaseEssentials.messaging || !firebaseEssentials.swRegistration) {
        console.warn("[NotificationContext] Android: Firebase essentials not available for initial check.");
        // fcmError would be set by ensureFirebase if SW registration failed
        return;
      }
      const { messaging, swRegistration } = firebaseEssentials;

      setPermission(Notification.permission);
      try {
        if (Notification.permission === "granted") {
          console.log("[NotificationContext] Android: Permission already granted. Attempting to get token.");
          const existingToken = await getToken(messaging, {
            vapidKey: process.env.REACT_APP_VAPID_KEY,
            serviceWorkerRegistration: swRegistration // Pass the specific SW registration
          });
          // ... rest of your logic for handling existingToken ...
          if (existingToken) {
            console.log("[Context] Existing Android FCM token found:", existingToken);
            setFcmToken(existingToken);
            setFcmError(null);
            if(user){
              await saveFCMTokenToBackend(existingToken, 'android');
            }
          } else {
            console.warn("[Context] Android permission is granted, but no token found initially (getToken returned null/empty).");
            setFcmError("Android: Permission granted, but no token found initially.");
            setFcmToken(null);
            setIsSubscribed(false);
          }
        } else {
            setIsSubscribed(false);
            console.log("[NotificationContext] Android: Notification permission not granted initially:", Notification.permission);
        }
      } catch (e) {
        console.error("[NotificationContext] Android getToken error on initial check:", e);
        setFcmError(`Android: Error on initial token check: ${e.message || String(e)}`);
        setFcmToken(null);
        setIsSubscribed(false);
      }
    })();
  }, [ANDROID_APP, ensureFirebase, user, saveFCMTokenToBackend]); // ensureFirebase is stable

  /* ------------ Auto-save token when user/token becomes available (for both platforms) ----- */
  useEffect(() => {
    // This effect ensures that if a token is obtained (fcmToken state is set)
    // AND the user is logged in (user state is set)
    // AND we are not yet marked as subscribed (isSubscribed state is false)
    // THEN we attempt to save the token to the backend.
    if (fcmToken && user && !isSubscribed) {
      if (fcmError) { 
        console.log("[Context] Auto-save: FCM token has an acquisition error, not attempting to save:", fcmError);
        return;
      }
      // Determine platform again, as this effect is general
      const platform = IOS_APP ? 'ios' : (ANDROID_APP ? 'android' : 'unknown');

      if (platform !== 'unknown' && !fcmToken.startsWith("ERROR_")) {
        console.log(`[Context] Auto-save: fcmToken ('${fcmToken.substring(0,10)}...'), user, and !isSubscribed are true. Platform: ${platform}. Attempting save.`);
        saveFCMTokenToBackend(fcmToken, platform).then(saved => {
          if (saved) {
            console.log("[Context] Auto-save: Dedicated save successful.");
          } else {
            console.warn("[Context] Auto-save: Dedicated save via useEffect failed.");
          }
        });
      } else {
        console.log("[Context] Auto-save: Not attempting to save invalid token or for unknown platform. Token:", fcmToken, "Platform:", platform);
      }
    }
  }, [fcmToken, user, isSubscribed, saveFCMTokenToBackend, fcmError, IOS_APP, ANDROID_APP]);


  /* ------------ Prompt visibility -------------------------------------- */
  useEffect(() => {
    // ... (Prompt visibility logic remains the same as your working version)
    if (IOS_APP) {
      if (!initialIOSCheckDone) {
        setShouldShowPrompt(false); return;
      }
      const showForNotDetermined = iosPermissionStatus === "notDetermined" && !isSubscribed;
      const showForDenied = iosPermissionStatus === "denied" && !isSubscribed && (!fcmToken || !!fcmError) ; 
      setShouldShowPrompt(showForNotDetermined || showForDenied);
    } else if (ANDROID_APP) {
      const show = (permission === "default" && !isSubscribed) || 
                   (permission === "granted" && !isSubscribed && (!fcmToken || !!fcmError));
      setShouldShowPrompt(show);
    } else {
      setShouldShowPrompt(false);
    }
  }, [initialIOSCheckDone, iosPermissionStatus, isSubscribed, fcmToken, fcmError, permission, IOS_APP, ANDROID_APP]);

  /* ------------ Subscribe (Android) ------------------------------------ */
  const requestPermissionAndSubscribe = useCallback(async () => {
    if (!ANDROID_APP) return false;

    const firebaseEssentials = await ensureFirebase();
    if (!firebaseEssentials || !firebaseEssentials.messaging || !firebaseEssentials.swRegistration) {
      setFcmError("Android: Firebase essentials not available for subscribe.");
      return false;
    }
    const { messaging, swRegistration } = firebaseEssentials;
    // ... rest of your requestPermissionAndSubscribe logic ...
    // Ensure you use swRegistration when calling getToken:
    // const token = await getToken(messaging, {
    //   vapidKey: process.env.REACT_APP_VAPID_KEY,
    //   serviceWorkerRegistration: swRegistration
    // });
    // ...
    let currentBrowserPermission = Notification.permission;
    if (currentBrowserPermission === "default") {
        currentBrowserPermission = await Notification.requestPermission();
        setPermission(currentBrowserPermission);
    }
    if (currentBrowserPermission !== "granted") {
      setShouldShowPrompt(currentBrowserPermission === "default");
      if (currentBrowserPermission === "denied") setFcmError("Android: Notification permission denied by user.");
      return false;
    }
    setPermission("granted");
    setFcmError(null);
    try {
      console.log("[NotificationContext] Android: Requesting new token with VAPID key and SW registration.");
      const token = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_VAPID_KEY,
        serviceWorkerRegistration: swRegistration // Pass the specific SW registration
      });
      if (!token) {
        setFcmError("Android: getToken returned an empty token after permission grant.");
        setFcmToken(null); throw new Error("FCM token vide");
      }
      console.log("[Context] Android FCM token obtained:", token);
      setFcmToken(token);
      setFcmError(null);
      const saved = await saveFCMTokenToBackend(token, 'android');
      if (saved) {
        setIsSubscribed(true); refreshUserProfile?.(); setShouldShowPrompt(false); return true;
      }
      return false; // save failed
    } catch (err) {
      console.error("[Context] Android subscribe error:", err);
      setFcmError(`Android: Error requesting permission/subscribing: ${err.message || String(err)}`);
      setFcmToken(null);
      if (permission !== "default") setShouldShowPrompt(false);
      return false;
    }
  }, [ANDROID_APP, ensureFirebase, refreshUserProfile, saveFCMTokenToBackend, permission]); // ensureFirebase is stable

  /* ------------ Un-subscribe (Android) --------------------------------- */
  const unsubscribe = useCallback(async () => {
    // ... (Android unsubscribe logic remains the same as your working version)
    if (!ANDROID_APP || !fcmToken) return false; // Added fcmToken check
    const messaging = await ensureFirebase();
    if (!messaging) return false;
    try {
      await deleteToken(messaging);
      // Backend deletion call
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/fcm/delete_fcm_token`, { // Use your delete URL
          method: "POST", headers: { "Content-Type": "application/json" },
          credentials: "include", body: JSON.stringify({ token: fcmToken }),
      });
      setIsSubscribed(false); setFcmToken(null); setPermission("default"); setFcmError(null); refreshUserProfile?.();
      return true;
    } catch (e) {
      console.error("[NotificationContext] Android unsubscribe error", e);
      setFcmError(`Android: Error during unsubscribe: ${e.message || String(e)}`);
      setIsSubscribed(false); setFcmToken(null); setPermission("default");
      return false;
    }
  }, [ANDROID_APP, ensureFirebase, fcmToken, refreshUserProfile]);

  const deferPrompt = useCallback(() => {
    setShouldShowPrompt(false);
  }, []);

  const value = {
    permission, iosPermissionStatus, isSubscribed, shouldShowPrompt,
    fcmToken, fcmError, requestPermissionAndSubscribe, unsubscribe,
    deferPrompt, saveFCMTokenToBackend, requestIOSTokenManually,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;